/**
 * 用量分布柱状图（按模型 token 数）。
 *
 * @module components/charts/UsageBarChart
 */
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import Box from '@mui/material/Box';
import { formatNumber } from '@/utils/format';

/** 柱状图数据项。 */
export interface UsageBarDatum {
  /** 模型名。 */
  model: string;
  /** token 数。 */
  tokens: number;
}

/** UsageBarChart 属性。 */
export interface UsageBarChartProps {
  /** 按模型聚合数据。 */
  data: UsageBarDatum[];
  /** 图表高度。 */
  height?: number;
}

/** 柱状图配色。 */
const BAR_COLORS = [
  '#4f46e5',
  '#0ea5e9',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#ec4899',
];

/**
 * 用量分布柱状图。
 *
 * @param props 组件属性。
 * @returns JSX 元素。
 */
export default function UsageBarChart({
  data,
  height = 280,
}: UsageBarChartProps): JSX.Element {
  return (
    <Box sx={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
          <XAxis
            dataKey="model"
            tick={{ fontSize: 12 }}
            interval={0}
            angle={-15}
            textAnchor="end"
            height={60}
          />
          <YAxis
            tick={{ fontSize: 12 }}
            width={56}
            tickFormatter={(v: number): string => formatNumber(v)}
          />
          <Tooltip
            formatter={(value: number | string): string =>
              `${formatNumber(value)} tokens`
            }
          />
          <Bar dataKey="tokens" radius={[6, 6, 0, 0]}>
            {data.map((entry, index) => (
              <Cell
                key={entry.model}
                fill={BAR_COLORS[index % BAR_COLORS.length]}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}
